// src/components/PermissionRoute.jsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../context/AuthContext';
import { Typography, Alert } from 'antd';

const { Text } = Typography;

// `requiredPermissions` is an AND: every entry must hold.
// `anyOf` is an OR: any one entry is enough. Use it where a screen is reachable
// through either a coarse module or a finer per-page one (e.g. the purity pages,
// which accept purityAnalyzer:<action> or their own module). Passing both means
// "all of requiredPermissions AND at least one of anyOf".
const PermissionRoute = ({ requiredPermissions = [], anyOf = [], children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { canViewModule, canEditModule, canViewCreateModule, can, isWildcardAdmin } = usePermissions();

  const holds = ({ module, action }) => {
    switch (action) {
      case 'view':
        return canViewModule(module);
      case 'edit':
        return canEditModule(module);
      case 'create':
        return canViewCreateModule(module);
      default:
        return can(module, action);
    }
  };

  const isAuthorized = requiredPermissions.every(holds)
    && (anyOf.length === 0 || anyOf.some(holds));

  const isAllowed = isWildcardAdmin || isAuthorized;

  // Redirect to login after 2 seconds when unauthorized
  useEffect(() => {
    if (!isAllowed) {
      const timer = setTimeout(() => {
        logout();
        navigate('/login', { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAllowed, logout, navigate]);

  if (isAllowed) {
    return children;
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <Alert
        message="Unauthorized Access"
        description="You do not have permission to access this resource. Please contact an administrator."
        type="error"
        showIcon
      />
      <Text type="secondary" style={{ display: 'block', marginTop: '10px' }}>
        You will be redirected to the login page shortly.
      </Text>
    </div>
  );
};


export default PermissionRoute;
